/**
 * Resolver a que tiendas puede entrar el usuario, y cual esta seleccionada.
 *
 * El problema
 * -----------
 * La tenencia vive en la tabla `user_store_access` del backend y NO viaja en el
 * JWT. Es una decision explicita de `src/services/auth.py`: si estuviera en el
 * token, revocarle una tienda a alguien tardaria lo que tarde el token en
 * vencer. La contracara es que el front no puede decodificar el JWT y saber que
 * mostrar: tiene que preguntar.
 *
 * Y hoy **no hay un endpoint que devuelva la tenencia del usuario**. No existe
 * un `GET /me`. Lo que hay es:
 *
 *   - `GET /stores`             -> devuelve TODAS las tiendas, sin filtrar por
 *                                  usuario. Es deuda conocida y esta anotada en
 *                                  `src/api/deps.py::UNSCOPED_LIST_ENDPOINTS`.
 *   - `GET /stores/{store_id}`  -> nombra la tienda en el path, asi que
 *                                  `enforce_authentication` SI aplica tenencia:
 *                                  200 si esta concedida, 403 si no.
 *
 * La solucion, y por que esta y no otra
 * -------------------------------------
 * Se usa `GET /stores` como lista de **candidatos** y se confirma cada uno con
 * `GET /stores/{id}`. La que responde 403 se descarta del selector.
 *
 * Lo importante: la autoridad sigue siendo el servidor. El front no decide nada
 * -- lee un veredicto. Filtrar en el cliente seria seguridad de mentira, pero
 * esto no es filtrar: es preguntar N veces algo que deberia poder preguntarse
 * una sola vez.
 *
 * Lo que esta solucion NO es
 * --------------------------
 * Escalable. Son N+1 requests, aceptable con dos tiendas (que es el caso real:
 * Barrio Chino y Barrio Chino AM) y malo con veinte. Ademas revela la
 * existencia y el nombre de tiendas ajenas via `GET /stores`, que es
 * exactamente la deuda que documenta `UNSCOPED_LIST_ENDPOINTS`.
 *
 * El arreglo correcto es del backend: un `GET /api/v1/me` que devuelva
 * `{user_id, email, stores: [{store_id, role}]}` a partir del `Principal` que
 * `enforce_authentication` ya tiene resuelto en `request.state.principal`. Con
 * eso esto se vuelve una sola llamada y ademas trae el rol, que hoy el panel no
 * puede conocer. Queda reportado como pendiente: escribirlo requiere tocar
 * `src/`, que no es de este scaffold.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { ApiError } from "../api/client";
import { getStore, listStores } from "../api/endpoints";
import type { Store } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useAsync, type AsyncState } from "../components/useAsync";

/** Clave de localStorage para recordar la tienda elegida entre recargas. */
const SELECTED_STORE_KEY = "barrio-chino:selected-store-id";

export interface StoreAccessState {
  /** Tiendas confirmadas por el servidor para este usuario. */
  readonly state: AsyncState<readonly Store[]>;
  /** Tienda seleccionada, o `null` si todavia no hay ninguna. */
  readonly selected: Store | null;
  readonly selectStore: (storeId: number) => void;
  readonly reload: () => void;
}

const StoreAccessContext = createContext<StoreAccessState | null>(null);

/**
 * Confirmar contra el servidor cuales de las tiendas candidatas son accesibles.
 *
 * Un 403 significa "no concedida" y se descarta en silencio: es la respuesta
 * esperada, no una falla. Cualquier otro error se propaga, porque no poder
 * distinguir "sin acceso" de "la API se cayo" llevaria a mostrar un selector
 * vacio cuando el problema es otro.
 */
async function confirmAccessibleStores(
  api: ReturnType<typeof useAuth>["api"],
  candidates: readonly Store[],
  signal: AbortSignal,
): Promise<Store[]> {
  const results = await Promise.all(
    candidates.map(async (candidate) => {
      try {
        return await getStore(api, candidate.id, { signal });
      } catch (error) {
        if (error instanceof ApiError && error.isForbidden) return null;
        throw error;
      }
    }),
  );

  return results.filter((store): store is Store => store !== null);
}

function readStoredStoreId(): number | null {
  try {
    const raw = window.localStorage.getItem(SELECTED_STORE_KEY);
    if (raw === null) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    // Modo privado o storage bloqueado: no recordar la eleccion es aceptable.
    return null;
  }
}

function writeStoredStoreId(storeId: number): void {
  try {
    window.localStorage.setItem(SELECTED_STORE_KEY, String(storeId));
  } catch {
    /* idem */
  }
}

export function StoreAccessProvider({ children }: { readonly children: ReactNode }) {
  const { api, session } = useAuth();
  const [selectedId, setSelectedId] = useState<number | null>(readStoredStoreId);

  const userId = session?.user.id ?? null;

  const { state, reload } = useAsync<readonly Store[]>(
    async (signal) => {
      const candidates = await listStores(api, { signal });
      return confirmAccessibleStores(api, candidates, signal);
    },
    // Se recarga al cambiar de usuario: la tenencia es por usuario, y quedarse
    // con la lista del anterior seria mostrar tiendas que este no puede ver.
    [api, userId],
    userId !== null,
  );

  const stores = state.kind === "ready" ? state.data : [];

  // La seleccion se deriva de lo que el servidor confirmo: si el id guardado ya
  // no esta concedido (le revocaron el acceso entre sesiones), cae a la primera
  // disponible en vez de quedar apuntando a una tienda prohibida.
  const selected = useMemo<Store | null>(() => {
    if (stores.length === 0) return null;
    const stored = stores.find((store) => store.id === selectedId);
    return stored ?? stores[0] ?? null;
  }, [stores, selectedId]);

  const selectStore = useCallback((storeId: number) => {
    setSelectedId(storeId);
    writeStoredStoreId(storeId);
  }, []);

  const value = useMemo<StoreAccessState>(
    () => ({ state, selected, selectStore, reload }),
    [state, selected, selectStore, reload],
  );

  return (
    <StoreAccessContext.Provider value={value}>{children}</StoreAccessContext.Provider>
  );
}

/** @throws Si se usa fuera de `StoreAccessProvider`. */
export function useStoreAccess(): StoreAccessState {
  const value = useContext(StoreAccessContext);
  if (value === null) {
    throw new Error("useStoreAccess() requiere estar dentro de <StoreAccessProvider>");
  }
  return value;
}
